import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Message} from 'com/gongxigames/math/core/game/message/Message';
import {Field} from 'com/gongxigames/math/core/game/message/Field';
import {TotalBet} from 'com/gongxigames/math/core/game/TotalBet';
import {Bonus} from 'com/gongxigames/math/core/game/engine/bonus/Bonus';
import {GameClientProxy} from 'com/gongxigames/math/core/client/GameClientProxy';
import {GameClientStub} from 'com/gongxigames/math/core/client/GameClientStub';
import {Session} from 'com/gongxigames/math/core/game/session/Session';
import {SlotEngine} from 'com/gongxigames/math/core/game/SlotEngine';
import {IBuyFeatureLogic} from '@gongxigames/framework/slot/services/logic/buyFeature/IBuyFeatureLogic';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {IPromotionLogic, IPromotionLogicInterface} from '@gongxigames/framework/promotions';
import { CoreDI, ISettings, lazyInject } from '@gongxigames/framework/core';
import { SlotSettingsParameters } from '@gongxigames/framework/slot';

interface BuyFeatureSession extends Session {
    getBuyFeatureMultiplier(message: Message): number;
    getBuyFeatureType(message: Message): string;
    getBuyFeaturePrice(totalBet: TotalBet, message: Message): number;
    isBuyFeatureSession(): boolean;
    isBuyFeatureGame(): boolean;
}

export type BuyFeatureType = 'freespin' | 'minispin' | 'respin' | 'select';

export class BuyFeatureLogicWrapper<S extends Session = Session, E extends SlotEngine<S> = SlotEngine<S>, BuyFeatureT = BuyFeatureType> implements IBuyFeatureLogic<BuyFeatureT> {
    private _client: GameClientProxy<S, E> | GameClientStub<S, E>;
    private _isRequest = false;
    private _isProgressStarted = false;
    private _isIndependentBet = false;
    private _promotion: IPromotionLogicInterface | IPromotionLogic;

    @lazyInject(CoreDI.Settings)
    private _settings!: ISettings<SlotSettingsParameters>;

    constructor(client: GameClientProxy<S, E> | GameClientStub<S, E>, promotion: IPromotionLogicInterface | IPromotionLogic) {
        this._client = client;

        if (slotStore.slot_isGameStateMachineActive.value && slotStore.slot_gameProvider.value === 'koala') {
            this.isIndependentBet = true;
        }
        this._promotion = promotion;
    }

    private getSession(): BuyFeatureSession {
        return this._client.getSession() as unknown as BuyFeatureSession;
    }

    private getBuyFeatureMessage(): Message {
        const message = new Message(this.getAction());

        // @ts-ignore
        message.addField(Field.BUY_FEATURE_TYPE, this.getSession().getBuyFeatureType(message));

        return message;
    }

    public getType(): BuyFeatureT {
        const message = new Message(this.getAction());

        return this.getSession().getBuyFeatureType?.(message) as unknown as BuyFeatureT;
    }

    public isInProgress(type: BuyFeatureT): boolean {
        return this.isBuyFeatureGame() && this.getType() === type && this.isProgressStarted;
    }

    public getMultiplier(): number {
        return this.getSession().getBuyFeatureMultiplier(this.getBuyFeatureMessage());
    }

    public getPrice(bet: number): number {
        const totalBet = new TotalBet(this.getIndependentBet(bet), this._client.getEngine().getMaxWager());

        return this.getSession().getBuyFeaturePrice(totalBet, this.getBuyFeatureMessage());
    }

    public getIndependentBet(bet: number): number {
        if (this.isIndependentBet) {
            return slotStore.logic_buyFeatureBet.value;
        }

        return bet;
    }

    public setProgressStarted(): void {
        if (this.isBuyFeatureGame()) {
            this._isProgressStarted ||= this.getSession().isBuyFeatureSession();
        }
    }

    public get isProgressStarted(): boolean {
        this.setProgressStarted();

        return this._isProgressStarted;
    }

    public set isProgressStarted(value: boolean) {
        this._isProgressStarted = value;
    }

    public get isRequest(): boolean {
        return this._isRequest;
    }

    public set isRequest(value: boolean) {
        this._isRequest = value;
    }

    public getBuyFeatureOrUsualBonus(bonusId: number = Bonus.FREE_GAMES_BONUS): number {
        // @ts-ignore
        return this.isProgressStarted ? Bonus.BUY_FEATURE_BONUS : bonusId;
    }

    public getBuyFeatureOrUsualAction(action: Action): number {
        return this.isProgressStarted ? this.getAction() : action;
    }

    public isBuyFeatureGame(): boolean {
        return Boolean(this.getSession().isBuyFeatureGame?.());
    }

    public isAction(action: Action): boolean {
        return action === this.getAction();
    }

    public getAction(): number {
        // @ts-ignore
        return Action.BUY_FEATURE;
    }

    public isMinBetAboveBuyFeatureLimit() {
        const bets = this.getSession().getGameSettings().bets;
        const minBet = Math.min(...bets);
        const maxBuyFeatureBet = this._settings.get('maxBuyFeatureBet')!;

        return minBet > maxBuyFeatureBet;
    }

    public shouldHide(): boolean {
        return this._promotion?.isActive?.() || this.isMinBetAboveBuyFeatureLimit();
    }

    public get isIndependentBet(): boolean {
        return this._isIndependentBet;
    }

    public set isIndependentBet(value: boolean) {
        this._isIndependentBet = value;
    }
}
